import type { GetCaseRecordResponse } from '@compass/client/types/case_record';
import type { Program } from '@compass/client/types/program';
import type { Team } from '@compass/client/types/team';
import { queryKeys } from '@services/query.config';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useReassignCase } from './useReassignCase';

interface UseWorkforceReassignParams {
  orgId: string | null | undefined;
  rawCasesById: Map<string, GetCaseRecordResponse>;
  /** Every team the user can pick in the team switcher — used to pick a target
   *  team when the case must be moved (the assignee isn't on its current team). */
  eligibleTeams: Team[];
  /** Programs where the user may use the console (with `teams[]`) — used to pick
   *  a target team that belongs to the case's program. */
  programs: Program[];
}

/**
 * Thin wrapper around `useReassignCase` that owns the lookup-by-id, the
 * target-team selection, and the (org, program) cache plumbing the mutation
 * needs.
 *
 * Reassign is never pre-blocked here: we always attempt the mutation and let
 * the backend validate. We do a best-effort pick of the `team_id` to send:
 *   - keep the case on its current team (send no `team_id`) when the new
 *     assignee is already on that (eligible) team — the pre-existing behavior;
 *   - otherwise move the case into an eligible team that the assignee belongs
 *     to and that serves the case's program + workflow type;
 *   - if no such team is found we still attempt with no `team_id` and let the
 *     backend decide (a rejected move surfaces the standard error toast from
 *     `useReassignCase`).
 *
 * Exposes:
 *   - `handleReassign(caseId, newAgentId)` — per-card flow; invalidates on settle.
 *   - `handleReassignNoInvalidate(caseId, newAgentId)` — bulk worker uses this
 *     so we can coalesce N parallel invalidations into one (`useBulkReassign`).
 *   - `invalidatePrograms(programIds)` — call once after a bulk batch settles to
 *     trigger a single refetch per touched program.
 */
export function useWorkforceReassign({
  orgId,
  rawCasesById,
  eligibleTeams,
  programs,
}: UseWorkforceReassignParams) {
  const { mutateAsync, isPending: isReassigning } = useReassignCase();
  const queryClient = useQueryClient();

  const reassign = useCallback(
    async (caseId: string, newAgentId: string, suppressInvalidate: boolean) => {
      if (!orgId) return;
      const targetCase = rawCasesById.get(caseId);
      if (!targetCase?.program_id) return;
      const programId = targetCase.program_id;

      const isTargetMember = (t: Team) =>
        t.manager_id === newAgentId || (t.member_ids ?? []).includes(newAgentId);

      // Best-effort target team. `undefined` = keep the case on its current
      // team (backend validates the assignee against it). Only move when the
      // assignee isn't already on the case's current team.
      let teamId: string | undefined;
      const currentTeam = eligibleTeams.find(t => t.id === targetCase.team_id);
      if (!(currentTeam && isTargetMember(currentTeam))) {
        const program = programs.find(p => p.id === programId);
        const workflowType = targetCase.workflow_type ?? '';
        teamId = eligibleTeams.find(
          t =>
            isTargetMember(t) &&
            !!t.id &&
            !!program?.teams?.includes(t.id) &&
            // When the case record carries no workflow type (data anomaly), fall
            // back to program-only matching — the backend resolves the workflow
            // type itself and has the final say on the move.
            (!workflowType || (t.workflow_types ?? []).includes(workflowType)),
        )?.id;
      }

      await mutateAsync({
        organization_id: orgId,
        program_id: programId,
        case_record_id: caseId,
        assigned_user_id: newAgentId,
        team_id: teamId,
        programId,
        suppressInvalidate,
      });
    },
    [orgId, rawCasesById, mutateAsync, eligibleTeams, programs],
  );

  const handleReassign = useCallback(
    (caseId: string, newAgentId: string) => reassign(caseId, newAgentId, false),
    [reassign],
  );

  const handleReassignNoInvalidate = useCallback(
    (caseId: string, newAgentId: string) => reassign(caseId, newAgentId, true),
    [reassign],
  );

  const invalidatePrograms = useCallback(
    (programIds: Iterable<string>) => {
      if (!orgId) return;
      const seen = new Set<string>();
      for (const programId of programIds) {
        if (!programId || seen.has(programId)) continue;
        seen.add(programId);
        queryClient.invalidateQueries({
          queryKey: queryKeys.workforce.casesByProgramPrefix(orgId, programId),
        });
      }
    },
    [orgId, queryClient],
  );

  return {
    handleReassign,
    handleReassignNoInvalidate,
    invalidatePrograms,
    isReassigning,
  };
}
